/**
 * <s-sticky-buy> — buy bar for phone, tablet and desktop. Shows when the main product form's submit button is off-screen
 * (and the page has been scrolled past it), mirrors the current price and sold-out state, and
 * submits the main form via the button's form attribute.
 */
class SStickyBuy extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById('product-form-main');
    this.add = this.querySelector('[data-sticky-add]');
    this.price = this.querySelector('[data-sticky-price]');
    this.variant = this.querySelector('[data-sticky-variant]');
    this.addLabel = this.add?.dataset.addLabel;
    this.soldLabel = this.add?.dataset.soldLabel;
    if (!this.form || !this.add) return;
    this.add.addEventListener('click', (event) => {
      if (!this.needsSelection) return;
      event.preventDefault();
      this.scrollToPicker();
    });
    this.observer = new IntersectionObserver(([entry]) => {
      this.offScreen = !entry.isIntersecting;
      this.evaluate();
    });
    // Re-check on scroll too: a button already below the fold never changes intersection on its own.
    this.onScroll = () => this.evaluate();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.watch();
    // The product form partial re-renders on variant change; re-attach to the new button.
    document.addEventListener('variant:change', () => this.watch());
    // Option changes update the summary right away, before the product partial re-renders.
    this.onChange = (event) => {
      if (event.target.closest?.('#product-form-main')) requestAnimationFrame(() => this.sync());
    };
    document.addEventListener('change', this.onChange);
    this.mutation = new MutationObserver(() => this.watch());
    // Watch the whole buy column so price and form re-renders both refresh the bar.
    const root = document.querySelector('[data-quick-add-source]') || this.form.parentElement || this.form;
    this.mutation.observe(root, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mutation?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('change', this.onChange);
  }

  watch() {
    this.form = document.getElementById('product-form-main');
    // The real Add to cart button (name="add"); when sold out it is the primary button rendered in its place.
    // Never match option pills, which can be disabled for unavailable sizes.
    const target =
      this.form?.querySelector('button[name="add"]') ||
      this.form?.querySelector('button.button-primary, [inert] button, button[inert]');
    if (!target || target === this.target) return this.sync();
    this.observer.disconnect();
    this.target = target;
    this.observer.observe(target);
    this.sync();
  }

  // Visible price text only: the price block without its screen-reader labels.
  readPrice() {
    // The price row is a PRICE label then the price, so read the last child (the price itself).
    const block = document.querySelector("[data-product-price]")?.lastElementChild;
    if (!block) return '';
    const copy = block.cloneNode(true);
    copy.querySelectorAll('.sr-only, [hidden]').forEach((node) => node.remove());
    return copy.textContent.replace(/\s+/g, ' ').trim();
  }

  // Selected option values (e.g. "20x30 cm / Framed / White frame") from the variant picker.
  readVariant() {
    const picker = this.form?.querySelector('s-variant-picker');
    if (!picker) return '';
    const values = [];
    picker.querySelectorAll('input[type="radio"]:checked, select').forEach((el) => {
      if (el.value) values.push(el.value);
    });
    if (values.length === 0) {
      picker.querySelectorAll('[aria-checked="true"], [aria-pressed="true"]').forEach((el) => {
        const text = (el.dataset.value || el.getAttribute('aria-label') || el.textContent || '').trim();
        if (text) values.push(text);
      });
    }
    return values.join(' / ');
  }

  sync() {
    if (this.price) this.price.textContent = this.readPrice();
    if (this.variant) {
      const text = this.readVariant();
      this.variant.textContent = text;
      this.variant.hidden = text === '';
    }
    const needsSelection = Boolean(this.target?.hasAttribute('data-needs-selection'));
    this.needsSelection = needsSelection;
    const sold = !this.target || this.target.name !== 'add' || this.target.disabled || this.target.getAttribute('aria-disabled') === 'true';
    // While options are unchosen the button stays clickable and jumps to the variant picker.
    this.add.disabled = sold && !needsSelection;
    const selectLabel = this.add.dataset.selectLabel;
    // Mirror the main button's prompt ("Select size", "Select frame") so both always ask for the same option.
    const promptLabel = this.target?.querySelector('[data-select-label]')?.textContent.trim();
    if (needsSelection && (promptLabel || selectLabel)) this.add.textContent = promptLabel || selectLabel;
    else if (this.addLabel && this.soldLabel) this.add.textContent = sold ? this.soldLabel : this.addLabel;
  }

  // Scroll the variant picker into view (below the sticky header) and focus the first unchosen option.
  scrollToPicker() {
    const picker = document.getElementById('product-form-main')?.querySelector('s-variant-picker');
    if (!picker) return;
    const header = document.querySelector('header');
    const offset = (header?.getBoundingClientRect().height || 0) + 24;
    const top = picker.getBoundingClientRect().top + window.scrollY - offset;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    const unchosen = Array.from(picker.querySelectorAll('fieldset')).find((fieldset) => (
      !fieldset.querySelector('input[type="radio"]:checked') && !fieldset.querySelector('select option:checked:not([value=""])')
    ));
    const control = (unchosen || picker).querySelector('input[type="radio"]:not(:disabled), select');
    control?.focus({ preventScroll: true });
  }

  // Show when the main button is off-screen above (scrolled past), or off-screen below once the
  // shopper has started scrolling (tall desktop buy columns can push the button under the fold).
  evaluate() {
    if (!this.target) return;
    const top = this.target.getBoundingClientRect().top;
    const show = Boolean(this.offScreen) && (top < 0 || window.scrollY > 300);
    if (show !== this.hasAttribute('data-visible')) this.toggle(show);
  }

  toggle(show) {
    this.sync();
    this.toggleAttribute('data-visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
    this.add.tabIndex = show ? 0 : -1;
  }
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', SStickyBuy);
